import React, { useState } from 'react';
import { CheckboxRow, Modal, PillButton, Row, Stack, Text, Title } from 'library/ui';
import { Colors } from 'library';
import { styled } from '@mui/material';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { ShiftProviderUser } from 'models';
import { consoleLogInDev, useClient } from 'shared';

const CLEARINGHOUSE_LINK = 'https://crw.flclearinghouse.com/';

export default function ClearinghouseModal({
    provider_user,
    business_location_id,
    refresh,
    onClose,
}: {
    provider_user: ShiftProviderUser;
    business_location_id: number;
    refresh: () => void;
    onClose: () => void;
}) {
    const [confirmed, setConfirmed] = useState(false);
    const { loading, send } = useClient({
        url: 'api/staff/record-action/',
        options: { sendImmediately: false },
    });

    const fullName = `${provider_user.first_name} ${provider_user.last_name}`;

    function onComplete() {
        send({
            method: 'POST',
            body: {
                action: 'add_to_clearinghouse',
                provider_user_id: provider_user.id,
                business_location_id,
            },
        })
            .then(() => refresh())
            .catch((e) => {
                alert('An error occurred. Please try again. If this persists please reach out to support@join-tandem.com.');
                consoleLogInDev(e);
            })
            .finally(() => onClose());
    }

    return (
        <Modal onClose={onClose} width={560}>
            <Stack gap="lg">
                <Row gap="md" align="start">
                    <IconBadge>
                        <VerifiedUserIcon htmlColor={Colors.success} />
                    </IconBadge>
                    <Stack gap="sm">
                        <Title size="h2">Add to Clearinghouse</Title>
                        <Text tone="secondary" lineHeight="relaxed">
                            Go to the Clearinghouse portal to add {fullName} to your roster, then mark this step complete
                            once they&apos;ve been added.
                        </Text>
                    </Stack>
                </Row>
                <Row>
                    <PillButton
                        variant="secondary"
                        onClick={() => window.open(CLEARINGHOUSE_LINK, '_blank')}
                        icon={<OpenInNewIcon style={{ fontSize: 16 }} />}
                    >
                        Open Clearinghouse
                    </PillButton>
                </Row>
                <CheckboxRow
                    checked={confirmed}
                    onChange={setConfirmed}
                    label={`I've added ${provider_user.first_name} to my Clearinghouse roster`}
                />
                <Row gap="sm" justify="end">
                    <PillButton variant="secondary" onClick={onClose}>
                        Cancel
                    </PillButton>
                    <PillButton disabled={!confirmed || loading} onClick={onComplete}>
                        Mark Complete
                    </PillButton>
                </Row>
            </Stack>
        </Modal>
    );
}

const IconBadge = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: Colors.successBackground,
});
